import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import MuiCheckbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid2";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import { FormikProps } from "formik";
import RouterLink from "next/link";
import * as yup from "yup";

import { Users } from "@phosphor-icons/react/Users";
import { paths } from "config/paths";
import { ButtonCancel } from "~ui-components/components/atoms/button-cancel";
import {
	ColumnDef,
	DataTable,
} from "~ui-components/components/atoms/data-table";
import { UserGroup } from "~ui-components/types/__generated/gql/graphql";
import { useWebRouter } from "~utils/next-router/use-web-router";

export const GroupAccessSchema = yup
	.object({
		id: yup.string().required(),
		viewable: yup.boolean().required(),
		editable: yup.boolean().required(),
	})
	.required();

export const DatasetAccessSchema = yup.object({
	isPublic: yup.boolean().required(),
	groupsAccesses: yup.array().of(GroupAccessSchema).required(),
});

export type DatasetAccessFormValues = yup.InferType<typeof DatasetAccessSchema>;
export type GroupAccessItem = yup.InferType<typeof GroupAccessSchema>;

export interface DatasetAccessFormProps {
	form: FormikProps<DatasetAccessFormValues>;
	groups: UserGroup[];
	isGroupsLoading: boolean;
	isManageGroupAllowed: boolean;
}

export function DatasetAccessForm(props: DatasetAccessFormProps) {
	const { form } = props;

	return (
		<form onSubmit={form.handleSubmit}>
			<CardContent>
				<Stack
					divider={<Divider />}
					spacing={4}>
					<Stack spacing={3}>
						<Grid
							container
							spacing={3}>
							<Grid size={12}>
								<PublicAccess {...props} />
							</Grid>
							<Grid size={12}>
								<InternalAccess {...props} />
							</Grid>
						</Grid>
					</Stack>
				</Stack>
			</CardContent>
			<CardActions sx={{ justifyContent: "flex-end" }}>
				<ButtonCancel />
				<Button
					type="submit"
					variant="contained">
					Save changes
				</Button>
			</CardActions>
		</form>
	);
}

const PublicAccess = (props: DatasetAccessFormProps) => {
	const { form } = props;

	return (
		<Stack>
			<Typography variant="overline">public access</Typography>
			<Typography variant="caption">
				Enable this setting to make your data publicly viewable and downloadable
			</Typography>
			<Box
				display="flex"
				gap={2}
				alignItems="center">
				<Switch
					{...form.getFieldProps("isPublic")}
					checked={form.getFieldProps("isPublic").value}
				/>
				<Typography variant="body2">Public access</Typography>
			</Box>
		</Stack>
	);
};

const InternalAccess = (props: DatasetAccessFormProps) => {
	const { form, groups, isGroupsLoading } = props;

	const columns = [
		{
			formatter: (row) => (
				<div>
					<Typography
						color="text.primary"
						sx={{ whiteSpace: "nowrap" }}
						variant="subtitle2">
						{row.name}
					</Typography>
				</div>
			),
			name: "Access groups",
		},
		{
			formatter: (row) => {
				const currentGroupAccessIndex = form.values.groupsAccesses.findIndex(
					(g) => g.id === row.id,
				);

				return (
					<MuiCheckbox
						checked={
							!!form.values.isPublic ||
							!!form.values.groupsAccesses[currentGroupAccessIndex]?.editable ||
							!!form.values.groupsAccesses[currentGroupAccessIndex]?.viewable
						}
						disabled={
							!!form.values.isPublic ||
							!!form.values.groupsAccesses[currentGroupAccessIndex]?.editable
						}
						onChange={(_, checked) => {
							if (currentGroupAccessIndex === -1)
								void form.setFieldValue("groupsAccesses", [
									...form.values.groupsAccesses,
									{
										id: row.id,
										viewable: checked,
										editable: false,
									},
								]);
							else {
								void form.setFieldValue(
									"groupsAccesses",
									form.values.groupsAccesses.map((g) =>
										g.id === row.id
											? {
													...g,
													viewable: checked,
												}
											: g,
									),
								);
							}
						}}
					/>
				);
			},
			name: "View",
		},
		{
			formatter: (row) => {
				const currentGroupAccessIndex = form.values.groupsAccesses.findIndex(
					(g) => g.id === row.id,
				);

				return (
					<MuiCheckbox
						checked={
							!!form.values.groupsAccesses[currentGroupAccessIndex]?.editable
						}
						onChange={(_, checked) => {
							if (currentGroupAccessIndex === -1)
								void form.setFieldValue("groupsAccesses", [
									...form.values.groupsAccesses,
									{
										id: row.id,
										viewable: false,
										editable: checked,
									},
								]);
							else {
								void form.setFieldValue(
									"groupsAccesses",
									form.values.groupsAccesses.map((g) =>
										g.id === row.id
											? {
													...g,
													editable: checked,
												}
											: g,
									),
								);
							}
						}}
					/>
				);
			},
			name: "Edit",
		},
	] as ColumnDef<UserGroup>[];

	return (
		<Stack>
			<Typography variant="overline">internal access</Typography>
			<Card>
				{isGroupsLoading ? (
					<Box
						m={4}
						textAlign="center">
						<CircularProgress />
					</Box>
				) : (
					<>
						<DataTable<UserGroup>
							uniqueRowId={(row) => row.id}
							columns={columns}
							rows={groups}
						/>
						{!groups.length && <EmptyAccess {...props} />}
					</>
				)}
			</Card>
		</Stack>
	);
};

const EmptyAccess = ({ isManageGroupAllowed }: DatasetAccessFormProps) => {
	const router = useWebRouter();
	return (
		<Stack
			m={4}
			gap={2}
			alignItems="center">
			<Chip label={<Users size="16px" />} />
			<Typography variant="h6">Nothing to see here</Typography>
			<Typography variant="body2">
				No groups yet! Create your first group to start organizing users and
				collaborating with ease
			</Typography>
			{isManageGroupAllowed && (
				<div style={{ textWrap: "nowrap" }}>
					<Button
						component={RouterLink}
						variant="contained"
						href={paths.accountName.groups.create(router._query.accountName)}>
						Create a Group
					</Button>
				</div>
			)}
		</Stack>
	);
};
